import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, switchMap, tap, first, take, filter } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { Observable, EMPTY, of, forkJoin } from 'rxjs';
import * as OrganizationActions from '../actions/organization.actions';
import { ProductsService } from '@app/services/products/product.service';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { LocationsService } from '@app/services/locations/locations.service';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { selectCurrentItemState } from '../selectors/organization.selectors';
import { ProductStoredAreasService } from '@app/services/product-stored-areas/product-stored-areas.service';
import { IArea } from '@app/models/area.interface';
import { TranslateService } from '@ngx-translate/core';
import * as CoreActions from '@app/core/store/core.action';
import { JwtService } from '@app/core/services/jwt.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { DeleteLocationModalComponent } from '../../locations/components/delete-location-modal/delete-location-modal.component';

@Injectable()
export class OrganizationEffects {
  constructor(private actions$: Actions, private _categories: CategoriesService,
    private _areas: AreasService,
    private _psa: ProductStoredAreasService,
    private _store: Store, 
    private _products: ProductsService, 
    private _toastr: ToastrService,
    private store$: Store,
    public _translation: TranslateService,
    public _locations: LocationsService,
    private _matDialog: MatDialog,
    private _jwt: JwtService) { }

  loadAllSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadAllSettings),
      switchMap(action =>
        combineLatest([
          this._categories.getCategories(),
          this._areas.getAreas()
        ]).pipe(
          map(([
            categories,
            areas
          ]) => OrganizationActions.loadAllSettingsSuccess({
            categories,
            areas
          })),
          catchError(error => {
            return of(OrganizationActions.loadAllSettingsFailure({ error: error.message }))
          })
        )
      )
    );
  });

  loadCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadCategories),
      concatMap(() =>
        this._categories.getCategoryWithProducts().pipe(
          map(categories => OrganizationActions.loadCategoriesSuccess(categories)),
          catchError(error => of(OrganizationActions.loadCategoriesFailure({ error }))))
      )
    );
  });

  loadAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadAreas),
      concatMap(() =>
        this._areas.getAreaWithProducts().pipe(
          map(areas => OrganizationActions.loadAreasSuccess(areas)),
          catchError(error => of(OrganizationActions.loadAreasFailure({ error }))))
      )
    );
  });

  saveCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.saveCategory),
      switchMap((action) =>
        this._categories.add(action.form).pipe(
          map(response => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitcategory'),
                show_upgrade: true
              }));
              return OrganizationActions.saveCategoryFailure({ error: this._translation.instant('anErrorOcurred') });
            }
            else{
              const { category } = response;
              if (category !== undefined) {
                this.store$.dispatch(CoreActions.increaseCurrentLimit({ types: 'categories' }));
                return OrganizationActions.saveCategorySuccess(response);
              } else {
                return OrganizationActions.saveCategoryFailure({ error: this._translation.instant('anErrorOcurred') });
              }
            }
          }),
          catchError(error => {
            this._toastr.error(this._translation.instant(this.categorySaveErrorKey(error)));
            return of(OrganizationActions.saveCategoryFailure({ error }))
          }))
      )
    );
  });

  updateCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.updateCategory),
      switchMap((action) =>
        this._categories.update(action.form, action.prdCatID).pipe(
          tap((item) => this._store.dispatch(OrganizationActions.setCurrentItem({ item: item }))),
          map((category) => OrganizationActions.updateCategorySuccess({ category: category, prdCatID: action.prdCatID })),
          catchError(error => {
            this._toastr.error(this._translation.instant(this.categorySaveErrorKey(error)));
            return of(OrganizationActions.saveCategoryFailure({ error }))
          }))
      )
    );
  });

  deleteCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.deleteCategory),
      switchMap((action) => {
        let current;
        this._store.select(selectCurrentItemState).pipe(first()).subscribe(res => current = res);
        return this._categories.onDelete(current.prdCatID).pipe(
          map((category) => {
            this.store$.dispatch(CoreActions.decreaseCurrentLimit({ types: 'categories' }));
            return OrganizationActions.deleteCategorySuccess(category);
          }),
          tap(() => {
            setTimeout(() => {
              const selected = document.getElementById('DragAndDrop--selected');
              if (selected != null) {
                selected.click()
              }
            }, 100);
          }),
          // A 409 (the category still has sub-categories) would otherwise
          // end this effect for the rest of the session.
          catchError(error => {
            this._toastr.error(this._translation.instant(
              error?.error?.result === 'failed.hasChildren' ? 'categories.hasChildren' : 'anErrorOcurred'
            ));
            return of(OrganizationActions.saveCategoryFailure({ error }));
          }))
      })
    );
  });

  // Picks the toast for a rejected category save from the server's message.
  private categorySaveErrorKey(error: any): string {
    const message = String(error?.error?.message || '');
    if (message.includes('">"')) {
      return 'categories.nameNoSeparator';
    }
    if (message.includes('under itself')) {
      return 'categories.moveIntoItself';
    }
    return 'categoryMustUnique';
  }

  selectFirstRow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.selectFirstRow),
      switchMap((action) => {
        return of(1).pipe(
          map((category) => OrganizationActions.selectFirstRowSuccess()))
      }

      )
    );
  });

  deleteArea$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.deleteArea),
      switchMap((action) => {
        let current;
        this._store.select(selectCurrentItemState).pipe(first()).subscribe(res => current = res.prdAreaID);;
        return this._areas.onDelete().pipe(
          map(() => {
            this.store$.dispatch(CoreActions.decreaseCurrentLimit({ types: 'areas' }));
            return OrganizationActions.deleteAreaSuccess({ prdAreaID: current })
          }),
          tap(() => {
            setTimeout(() => {
              const selected = document.getElementById('DragAndDrop--selected');
              if (selected != null) {
                selected.click()
              }
            }, 100);
          }))
      }

      )
    );
  });

  addArea$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addArea),
      switchMap((action) =>
        this._areas.onAddArea(action.form).pipe(
          map((response) => {
            if (response.result !== undefined && response.result === 'failed.max') {
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitarea'),
                show_upgrade: true
              }));
            } else {
              const { area } = response;
              if (area !== undefined) {
                this.store$.dispatch(CoreActions.increaseCurrentLimit({ types: 'areas' }));
                return OrganizationActions.addAreaSuccess(response);
              }
            }
          }),
          catchError(error => {
            this._toastr.error(error.error.message);
            return of(null)
          })
        ),
      )
    );
  });


  /*addArea$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addArea),
      switchMap((action) =>
        this._areas.onAddArea(action.form).pipe(
          switchMap(area => {
            if (area.result !== undefined && area.result === 'failed.max') {
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitarea')
              }));
              return EMPTY;
            } else {
              const { _area } = area;
              if (_area !== undefined) {
                return OrganizationActions.addAreaSuccess(area);
              } else {
                return of(null);
              }
            }
          }),
          catchError(error => {
            this._toastr.error(error.error.message);
            return of(null);
          })
        )
      ),
      catchError(() => EMPTY)
    );
  });*/


  updateArea$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.updateArea),
      switchMap((action) =>
        this._areas.onUpdateArea(action.form, action.prdAreaID).pipe(
          tap((item) => this._store.dispatch(OrganizationActions.setCurrentItem({ item: item }))),
          map((area) => OrganizationActions.updateAreaSuccess({ area: area })),
          catchError(error => {
            this._toastr.error(error.error.message);
            return of(null)
          }))
      )
    );
  });

  loadMasterSortCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadMasterSortCategories),
      switchMap((action) =>
        this._products.onGetMasterSortByCategory(action.prdCatID).pipe(
          map((data) => OrganizationActions.loadMasterSortCategoriesSuccess({ data: data })))
      )
    );
  });

  removeFromMasterSortCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.removeFromMasterSortCategories),
      switchMap((action) =>
        this._products.onRemoveFromCategory([action.product.prdID]).pipe(
          filter((response: any) => !(response && response.noop)),
          map(() => OrganizationActions.removeFromMasterSortCategoriesSuccess({ product: action.product })),
          catchError(() => EMPTY)
        )
      )
    );
  });

  addFromMasterSortCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addFromMasterSortCategories),
      switchMap((action) =>
        this._products.onAddFromCategory([action.product.prdID]).pipe(
          tap(() => {
            let current;
            this._store.select(selectCurrentItemState).subscribe(res => current = res);
            this._toastr.success(`${action.product.prdNameEn} ${this._translation.instant('addedTo')} ${current.prdCatNameEn}`);
          }),
          map(() => OrganizationActions.addFromMasterSortCategoriesSuccess({ product: action.product })))
      )
    );
  });

  loadMasterSortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadMasterSortAreas),
      switchMap((action) =>
        this._products.onGetByArea(action.prdAreaID).pipe(
          map((data: any) => OrganizationActions.loadMasterSortAreasSuccess({ data: data })))
      )
    );
  });

  removeFromMasterSortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.removeFromMasterSortAreas),
      switchMap((action) =>
        this._products.onRemoveFromArea([action.product.prdID]).pipe(
          tap(() => {
            let current;
            this._store.select(selectCurrentItemState).pipe(first()).subscribe(res => current = res);
          }),
          map(() => OrganizationActions.removeFromMasterSortAreasSuccess({ product: action.product })))
      )
    );
  });

  addFromMasterSortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addFromMasterSortAreas),
      switchMap((action) =>
        this._products.onAddFromArea([action.product.prdID]).pipe(
          tap(() => {
            let area: IArea;
            this._store.select(selectCurrentItemState).pipe(take(1)).subscribe(response => area = response)
            this._toastr.success(`${action.product.prdNameEn} ${this._translation.instant('addedTo')} ${area.prdAreaNameEn}`);
          }),
          map(() => OrganizationActions.addFromMasterSortAreasSuccess({ product: action.product })))
      )
    );
  });

  addAllFromMasterSortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addAllFromMasterSortAreas),
      switchMap((action) => {
        const ids = action.products.map(item => item.prdID);
        return this._products.onAddFromArea(ids).pipe(
          map(() => OrganizationActions.addAllFromMasterSortAreasSuccess({ products: action.products, prdCatID: action.prdCatID })))
      }
      )
    );
  });

  addUncategorizedFromMasterSortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.addUncategorizedFromMasterSortAreas),
      switchMap((action) => {
        const ids = action.products.map(item => item.prdID);
        return this._products.onAddFromArea(ids).pipe(
          tap(() => {
            let current;
            this._store.select(selectCurrentItemState).subscribe(res => current = res);
            // this._toastr.success(`${action} added to ${current.prdAreaNameEn}`);
          }),
          map(() => OrganizationActions.addUncategorizedFromMasterSortAreasSuccess({ products: action.products })))
      }
      )
    );
  });

  sortAreas$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.sortAreas),
      switchMap((action) => {
        return this._psa.onSortAreas(action.products, action.area).pipe(
          map(() => OrganizationActions.sortAreasSuccess()))
      }
      )
    );
  });

  sortCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.sortCategories),
      switchMap((action) => {
        return this._categories.onSortCategories(action.products).pipe(
          map(() => OrganizationActions.sortCategoriesSuccess()))
      }
      )
    );
  });

  sortCategoriesByName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.sortCategoryByName),
      switchMap((action) => {
        return this._categories.onSortCategoriesByName(action.prdCatID).pipe(
          map(({ inCat }) => OrganizationActions.sortCategoryByNameSuccess({ inCat: inCat })))
      }
      )
    );
  });

  sortAreasByName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.sortAreaByName),
      switchMap((action) => {
        return this._areas.onSortAreasByName(action.prdAreaID).pipe(
          map(() => OrganizationActions.sortAreaByNameSuccess()))
      }
      )
    );
  });

  /* start multilocations effects */
  loadLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadLocations),
      switchMap(() => {
        return this._locations.onGetLocations('locName', 'asc').pipe(
          map(({ data })=> OrganizationActions.loadLocationsSuccess({ locations: data.locations, total: data.total })),
          catchError(error => of(OrganizationActions.loadLocationsFailure({ error }))))
      }
      )
    );
  });

  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.sortLocations),
      switchMap((action) => {
        return this._locations.onGetLocations(action.sort, action.direction).pipe(
          map(({ data })=> OrganizationActions.loadLocationsSuccess({ locations: data.locations, total: data.total })),
          catchError(error => of(OrganizationActions.loadLocationsFailure({ error }))))
      }
      )
    );
  });

  saveLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.saveLocation),
      switchMap((action) => {
        return this._jwt.onSaveLocation(action).pipe(
          map(({ location,message }) => {
            this._toastr.success(this._translation.instant(message), '', { timeOut: 5000 });
            return OrganizationActions.saveLocationSuccess({ location: location})
          }),
          catchError(error => {
            this._toastr.error(this._translation.instant(error.error.message), '', { timeOut: 5000 });
            return of(OrganizationActions.saveLocationFailure({ error }))
          })
        )
      })
    );
  });

  deleteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.deleteLocation),
      switchMap((action) => {
        return this._locations.onDeleteLocation(action.id).pipe(
          map((response: any) => {
            if (response?.data?.toastr?.key == 'toastr.NotDeleted' || response?.data?.toastr?.key == 'toastr.NotDeletedDefault') { 
              return OrganizationActions.noDeleteLocationSuccess({ id: action.id });
            }else{
              return OrganizationActions.deleteLocationSuccess({ id: action.id });
            }
          }),
          catchError(error => of(OrganizationActions.loadLocationsFailure({ error }))))
      }
      )
    );
  });

  reloadAfterDelete$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationActions.deleteLocationSuccess),
        tap(() => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        })
      );
    },
    { dispatch: false }
  );

  loadLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.loadLocation),
      switchMap((action) => {
        return this._locations.onGetLocation(action.id).pipe(
          map(location => OrganizationActions.loadLocationSuccess({ location })),
          catchError(error => of(OrganizationActions.loadLocationFailure({ error }))))
      }
      )
    );
  });

  updateLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.updateLocation),
      switchMap((action) => {
        return this._locations.onUpdateLocation(action.id, action.location).pipe(
          map(({ location, message }) => {
            this._toastr.success(this._translation.instant(message.message), '', { timeOut: 5000 });
            return OrganizationActions.updateLocationSuccess({ location: location })
          }),
          catchError(error => {
            this._toastr.error(this._translation.instant(error.error.message), '', { timeOut: 5000 });
            return of(OrganizationActions.updateLocationFailture({ error }))
          })
        )
      }
      )
    );
  });

  deleteLocationsShowErrorMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteLocationsShowErrorMessage),
      tap((action) => {
        this._locations.onGetUsersUniqueLocations(action.id).subscribe((users) => {
          if(users.count>0){
            
            let location_id = action.id;
            let counts_users = users.count; 
            const resultDelete = this._matDialog.open(DeleteLocationModalComponent, {
              minWidth: '30vw',
              maxWidth: '700px',
              data: {
                location_id: location_id,
                counts_users: counts_users
              }
            });

            resultDelete.afterClosed().subscribe((res) => {
              if(res == 'true'){
                this.store$.dispatch(OrganizationActions.deleteLocation({ id:action.id }));
                this.store$.subscribe((state: any) => {
                  if(state!=null && state.core!=null && state.core.me!=null && state.core.me.locID!=null){
                    const locId = state.core.me.locID;
                    if(action.id==locId){
                      this._jwt.onLogOut();
                    }
                  }
                });
              }
            });
          } else{ 
            const ref = this._matDialog.open(ConfirmModalComponent, {
              minWidth:'30vw',
              maxWidth: '700px',
              data:{
                key: `location`,
                deleteMessage: `${this._translation.instant('deleteConfirmSimple')}`,
                deletePassword: 'DELETE'
              },
            });

            ref.afterClosed().subscribe((res) => {
              if(res == 'true'){
                this.store$.dispatch(OrganizationActions.deleteLocation({ id:action.id }));
                this.store$.subscribe((state: any) => {
                  if(state!=null && state.core!=null && state.core.me!=null && state.core.me.locID!=null){
                    const locId = state.core.me.locID;
                    if(action.id==locId){
                      this._jwt.onLogOut();
                    }
                  }
                });
              }
            });
          }
        });
      })
    ),
    { dispatch: false }
  );

  assignNewLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationActions.assignNewLocation),
      switchMap((action) => {
        return this._locations.onAssignNewLocation(action.current_location,action.new_location).pipe(
          map((item: any) => {
            return OrganizationActions.deleteLocationSuccess({ id: action.new_location })
        }),
          catchError(error => of(OrganizationActions.loadLocationFailure({ error }))))
      }
      )
    );
  });
  /* end multilocations effects */

}
